import { usePageTitle } from "@/lib/usePageTitle";
import { ArrowRight, Check, Info, Phone } from "lucide-react";
import FadeIn from "@/components/site/FadeIn";
import Icon from "@/components/site/Icon";
import { PrimaryLink, CoralLink, PageHero } from "@/components/site/ui";
import { COMPANY } from "@/data/content";
import { useLocale } from "@/i18n/LocaleContext";

export default function Leistungen() {
  usePageTitle("services");
  const { t, path, dir } = useLocale();
  const flip = dir === "rtl" ? "lg:[direction:ltr]" : "lg:[direction:rtl]";
  return (
    <div data-testid="services-page">
      <PageHero
        eyebrow={t.servicesPage.eyebrow}
        title={t.servicesPage.title}
        subtitle={t.servicesPage.subtitle}
      />

      {/* Leistungsbereiche */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24 space-y-16 md:space-y-24">
          {t.services.map((s, i) => (
            <FadeIn key={s.id}>
              <div
                id={s.id}
                data-testid={`service-section-${s.id}`}
                className={`grid lg:grid-cols-2 gap-8 lg:gap-14 items-center scroll-mt-28 ${
                  i % 2 === 1 ? flip : ""
                }`}
              >
                <div style={{ direction: dir }}>
                  <span className="flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-light text-brand">
                    <Icon name={s.icon} className="w-8 h-8" />
                  </span>
                  <h2 className="mt-5 text-3xl md:text-4xl font-600 text-brand-dark break-words hyphens-auto">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                    {s.description}
                  </p>
                  {s.note && (
                    <p className="mt-4 flex items-start gap-2 rounded-xl bg-coral/10 border border-coral/25 p-4 text-base text-ink">
                      <Info className="w-5 h-5 mt-0.5 text-coral-hover shrink-0" />
                      {s.note}
                    </p>
                  )}
                </div>
                <div style={{ direction: dir }}>
                  <ul className="rounded-2xl bg-cream border border-border p-7 space-y-4">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-3 text-lg text-ink">
                        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand text-white shrink-0 mt-0.5">
                          <Check className="w-4 h-4" strokeWidth={3} />
                        </span>
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Kurzzeit-/Dauerpflege + Beratung */}
      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <FadeIn>
              <div className="rounded-2xl bg-white p-8 border border-border h-full">
                <h3 className="text-2xl font-600 text-brand-dark">
                  {t.servicesPage.shortLongTitle}
                </h3>
                <p className="mt-3 text-lg text-muted-foreground leading-relaxed">{t.servicesPage.shortLongText}</p>
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className="rounded-2xl bg-white p-8 border border-border h-full">
                <h3 className="text-2xl font-600 text-brand-dark">
                  {t.servicesPage.adviceTitle}
                </h3>
                <p className="mt-3 text-lg text-muted-foreground leading-relaxed">{t.servicesPage.adviceText}</p>
              </div>
            </FadeIn>
          </div>

          <FadeIn>
            <div className="mt-10 rounded-2xl bg-brand-dark text-white p-8 md:p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-600">
                {t.servicesPage.ctaTitle}
              </h2>
              <p className="mt-4 text-lg text-white/80 max-w-2xl mx-auto">{t.servicesPage.ctaLead}</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <PrimaryLink to={path("contact")} data-testid="services-contact-cta">
                  {t.common.requestConsultation} <ArrowRight className="w-5 h-5 rtl:rotate-180" />
                </PrimaryLink>
                <CoralLink href={`tel:${COMPANY.phoneRaw}`} data-testid="services-call-cta">
                  <Phone className="w-5 h-5" /> {COMPANY.phone}
                </CoralLink>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
